import * as React from 'react'
import { t } from 'ttag'
import { formatThousands } from '../../common/helpers/translations'
import NotificationsWrapper from '../../components/Notification/NotificationsWrapper'
import { StatisticsBar } from '../../components/StatisticsBar/StatisticsBar'
import { StatisticsCard } from '../../components/StatisticsBar/StatisticsCard/StatisticsCard'
import {
  CompanyId,
  CompanyStatistics,
  GetSpecificCompanyStatistics$Request,
  Lang,
  UserCompanyDto,
} from '../../services/api.types'
import { Card } from '../../components/Card/Card'
import Grid from '@material-ui/core/Grid'
import './Statistics.scss'
import { isBurgerHidden } from '../RegisterUser/RegisterUser'
import YumSmallSelect from '../../components/YumSmallSelect/YumSmallSelect'
import { yuMuuvPublicCompanyId } from '../Settings/Settings'
import { currentLang, isRtl } from '../../i18nInit'

export interface IStatisticsProps {
  companyStatistics: CompanyStatistics
  userCompanies: UserCompanyDto[]
  getSpecificCompanyStatistics(payload: GetSpecificCompanyStatistics$Request): void
}

export class Statistics extends React.PureComponent<IStatisticsProps> {
  state = {
    userCompany: this.props.userCompanies.filter((uc) => !uc.hideStatisticsView)[0],
  }

  static getStepsStats(totalSteps: number, roundsAroundTheWorld: number) {
    const y = formatThousands(totalSteps)
    let x: string | number = roundsAroundTheWorld
    const text =
      currentLang === Lang.EN
        ? `We're circling the earth... <b>${x}% (${y} steps)</b> of it is done`
        : t`We are circling the earth. <b>${x}% (${y} steps)</b> of it is done`
    if (roundsAroundTheWorld < 1) {
      x = Number(x * 100).toFixed(0)
      return <StatisticsCard type="steps" text={text} />
    } else {
      return (
        <StatisticsCard
          type="steps"
          text={t`We have circled <b>${x} times (${y} steps)</b> around the earth together`}
        />
      )
    }
  }

  static getIntensiveMinutesStats(co2EmissionPrevented: number, totalMin: number) {
    const x = formatThousands(totalMin)
    const y = String(formatThousands(Math.round(co2EmissionPrevented)))
    const text =
      currentLang === Lang.EN
        ? `We've collected ${x} intensity mins. <b>${y} g of CO2</b> gases are released by a car in this time`
        : t`We have collected ${x} intensity min. <b>${y} g of CO2</b> gases are released by a car in this time`
    return <StatisticsCard text={text} type="intensiveMinutes" />
  }

  componentDidMount(): void {
    this.loadCompanyStatistics()
  }

  loadCompanyStatistics(): void {
    const { getSpecificCompanyStatistics } = this.props
    const { userCompany } = this.state
    const companyId = userCompany?.companyId
    getSpecificCompanyStatistics({ companyId })
  }

  handleCompanyChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const { userCompanies } = this.props
    const { value } = event.target as { value: CompanyId }
    this.setState({ userCompany: userCompanies.find((company) => company.companyId === value) }, () =>
      this.loadCompanyStatistics(),
    )
  }

  getBurgerStats = (companyId: CompanyId, burgers: number): string => {
    const x = String(formatThousands(Math.round(burgers)))
    return companyId !== '0afa8503-baf3-4f7b-8e72-f133f716204e'
      ? currentLang === Lang.EN
        ? `We've spent <b>${x} Big Macs</b> in calories`
        : t`We have spent <b>${x} Big Mac's</b> worth of calories`
      : t`We have spent <b>${x} Whopper's</b> worth of calories`
  }

  render() {
    const {
      userCompanies,
      companyStatistics: {
        companyStepsStatistics: { totalSteps, roundsAroundTheWorld },
        companyIntensiveMinutesStatistics: { totalMin, co2EmissionPrevented },
        companyEnergyStatistics: { bigMacs },
      },
    } = this.props
    const { userCompany } = this.state
    const companyId = userCompany?.companyId

    if (!companyId) {
      return (
        <Card>
          <h1 style={{ marginBottom: '1rem' }}>{t`Statistics`}</h1>
          Currently disabled for your company.
        </Card>
      )
    }

    const nonPublicCompanies = userCompanies.filter(({ companyId }) => companyId !== yuMuuvPublicCompanyId)
    const userCompaniesWithStatistics = nonPublicCompanies.filter(({ hideStatisticsView }) => !hideStatisticsView)

    const burgerStats = !isBurgerHidden(companyId) ? this.getBurgerStats(companyId, bigMacs) : undefined

    return (
      <Card>
        <NotificationsWrapper />
        <Grid
          container
          justifyContent="space-between"
          alignContent="center"
          alignItems="center"
          wrap="nowrap"
          style={{ marginBottom: '1rem' }}
        >
          <Grid item>
            <h1 style={{ marginBottom: 3 }}>{t`Statistics`}</h1>
          </Grid>
          {nonPublicCompanies.length > 1 && userCompaniesWithStatistics.length ? (
            <Grid
              item
              style={{ overflow: 'hidden', paddingLeft: isRtl ? 0 : '10px', paddingRight: isRtl ? '10px' : 0 }}
            >
              <YumSmallSelect
                variant="outlined"
                value={companyId}
                onChange={this.handleCompanyChange}
                /*inputProps={{ name: 'company', id: 'company' }}*/
              >
                {userCompaniesWithStatistics.map((userCompany: UserCompanyDto, keyIndex: number) => (
                  <option key={keyIndex} value={userCompany.companyId}>
                    {userCompany.companyName}
                  </option>
                ))}
              </YumSmallSelect>
            </Grid>
          ) : (
            ''
          )}
        </Grid>

        <StatisticsBar>
          {burgerStats ? <StatisticsCard text={burgerStats} type="calories" /> : ''}
          {Statistics.getIntensiveMinutesStats(co2EmissionPrevented, totalMin)}
          {Statistics.getStepsStats(totalSteps, roundsAroundTheWorld)}
        </StatisticsBar>
      </Card>
    )
  }
}
